import React from 'react';
import { Card } from '@components/atoms/Card';
import { Project } from '@models/project.model';
import { Button } from '@components/atoms/Button';
import ModeEditOutlinedIcon from '@mui/icons-material/ModeEditOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { Badge } from '@components/atoms/Badge';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { STATE_COLORS } from '@constants/stateColors';
import { EmptyData } from '@components/molecules/EmptyData';
import { format } from 'date-fns';
import AddIcon from '@mui/icons-material/Add';

interface ProjectsGridProps {
  projects?: Project[];
  onEditProject?: (projectId: string) => void;
  onRemoveProject?: (projectId: string) => void;
  onNavigateToProject?: (projectId: string) => void;
  onCreateProject?: () => void;
  // Sprint 25, story-128 (run 5). OWNER/ADMIN/PROJECT_MANAGER only — a SUPERINTENDENT keeps
  // read access to a client's project grid but not the edit/remove/create actions here.
  canManage?: boolean;
  // Sprint 52, story-269. The Past Projects tab reuses this grid with its own empty copy and no
  // "New Project" call to action (a new project is never a past one).
  emptyDescription?: string;
  hideCreate?: boolean;
}

function formatProjectTimeline(
  start: string | Date | null,
  end: string | Date | null,
): string {
  if (!start || !end) return 'N/A';
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return 'N/A';
  try {
    const startText = format(startDate, 'MMM do');
    const endText = format(endDate, 'MMMM do');
    return `${startText} - ${endText}`;
  } catch {
    return 'N/A';
  }
}

export const ProjectsGrid: React.FC<ProjectsGridProps> = ({
  projects,
  onEditProject,
  onRemoveProject,
  onNavigateToProject,
  onCreateProject,
  canManage = true,
  emptyDescription = 'No Active Projects Yet',
  hideCreate = false,
}) => {
  return (
    <div className="mt-4 h-full">
      {projects && projects.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 pb-6 mt-4">
          {(projects || []).map((project) => (
            <Card
              key={project.projectId}
              className="w-full flex flex-col h-full justify-between">
              <div className="relative flex gap-2 justify-between">
                <div 
                  className="flex gap-2 flex-col cursor-pointer flex-1"
                  onClick={() => project.projectId && onNavigateToProject?.(project.projectId)}
                >
                  <div className="flex gap-2 items-end">
                    {project.siteImage ? (
                      <img
                        alt={project.name}
                        src={project.siteImage}
                        className="w-28 h-28 object-cover rounded-lg"
                      />
                    ) : (
                      <div className="w-28 h-28 bg-gray-200 text-gray-500 dark:bg-gray-600 rounded-lg flex items-center justify-center">
                        <ImageOutlinedIcon sx={{ fontSize: '34px' }} />
                      </div>
                    )}
                    <Badge
                      className={`capitalize ${STATE_COLORS[project.state as keyof typeof STATE_COLORS]}`}
                      size="sm">
                      {project.state}
                    </Badge>
                  </div>
                  <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                    {project.name}
                  </div>
                  <div className="flex flex-col">
                    <p className="text-sm text-gray-900 dark:text-gray-100">
                      Address
                    </p>
                    <div className="text-sm text-gray-500 dark:text-gray-300">
                      {project.address}
                    </div>
                  </div>
                </div>
                {canManage && (
                  <div className="absolute top-0 right-0 flex gap-2 h-10">
                    <Button
                      variant="tertiary"
                      onlyIcon
                      onClick={() =>
                        project.projectId && onEditProject?.(project.projectId)
                      }>
                      <ModeEditOutlinedIcon sx={{ fontSize: '19px' }} />
                    </Button>
                    <Button
                      variant="tertiary"
                      onlyIcon
                      onClick={() =>
                        project.projectId && onRemoveProject?.(project.projectId)
                      }>
                      <CloseOutlinedIcon sx={{ fontSize: '19px' }} />
                    </Button>
                  </div>
                )}
              </div>
              <div 
                className="flex flex-col gap-2 mt-3 cursor-pointer"
                onClick={() => project.projectId && onNavigateToProject?.(project.projectId)}
              >
                <div className="flex justify-between gap-2 mt-3">
                  <div className="flex flex-col">
                    <p className="text-sm text-gray-900 dark:text-gray-100">
                      Budget
                    </p>
                    <div className="text-sm text-gray-500 dark:text-gray-300">
                      {project.budget || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-900 dark:text-gray-100">
                      Date Range
                    </p>
                    <div className="text-sm text-gray-500 dark:text-gray-300">
                      {formatProjectTimeline(
                        project.startDate,
                        project.endDate,
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
      {projects?.length === 0 && (
        <EmptyData
          textButton={canManage && !hideCreate ? 'New Project' : undefined}
          onClickButton={canManage && !hideCreate ? onCreateProject : undefined}
          description={emptyDescription}
          iconButton={
            canManage && !hideCreate ? <AddIcon sx={{ fontSize: '16px' }} /> : undefined
          }
        />
      )}
    </div>
  );
};
